import { X } from 'lucide-react';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { londonClock } from '../lib/format';

// One app on the phone's home screen (Lagos Life's phone is a grid of apps; Messages is one of them).
export type PhoneApp = {
  id: string;
  label: string;
  icon: string;
  // the icon's background, a palette token
  tone: 'green' | 'blue' | 'gold' | 'red' | 'purple' | 'teal' | 'navy' | 'orange';
  badge?: number;
  onOpen: () => void;
};

const londonDate = (now: Date) =>
  now.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Europe/London' });

// how long the phone takes to slide up or away (matches the CSS), and how far it is pulled down to put away
const SLIDE_MS = 350;
const PULL_TO_CLOSE = 120;
// the phone follows a finger pulling it down a little slower than the finger, so it feels held
const PULL_GIVE = 0.6;
const reducedMotion = () => window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;

// The Phone tab: a phone in the hand, as in Lagos Life. It slides up from the bottom of the screen over the
// game: a dark frame round the screen, the status bar with the island, the time in London and the signal,
// a wallpaper with London's skyline, the clock, and an app for each part of your life. It goes away with
// Close, the home bar, a tap outside it, Escape, the back gesture, or by pulling the status bar down.
export function PhoneScreen({ open, apps, onClose }: { open: boolean; apps: PhoneApp[]; onClose: () => void }) {
  // still drawn while it slides away after closing
  const [shown, setShown] = useState(open);
  if (open && !shown) setShown(true);
  useEffect(() => {
    if (open || !shown) return;
    const timer = window.setTimeout(() => setShown(false), reducedMotion() ? 0 : SLIDE_MS);
    return () => window.clearTimeout(timer);
  }, [open, shown]);
  return shown ? <Phone open={open} apps={apps} onClose={onClose} /> : null;
}

// the phone itself, made afresh each time it comes out (so the clock is right at once)
function Phone({ open, apps, onClose }: { open: boolean; apps: PhoneApp[]; onClose: () => void }) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 20_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // pulling the status bar down takes the phone with it; far enough and it is put away
  const press = useRef<{ y: number; id: number } | null>(null);
  const [pull, setPull] = useState<number | null>(null);
  const grab = (event: PointerEvent<HTMLDivElement>) => {
    press.current = { y: event.clientY, id: event.pointerId };
    event.currentTarget.setPointerCapture?.(event.pointerId);
    setPull(0);
  };
  const drag = (event: PointerEvent<HTMLDivElement>) => {
    if (press.current?.id !== event.pointerId) return;
    setPull(Math.max(0, event.clientY - press.current.y) * PULL_GIVE);
  };
  const release = (event: PointerEvent<HTMLDivElement>) => {
    if (press.current?.id !== event.pointerId) return;
    const pulled = event.clientY - press.current.y;
    press.current = null;
    setPull(null);
    if (pulled > PULL_TO_CLOSE) onClose();
  };

  const time = londonClock(now);
  return (
    <div
      className={`phone-screen${open ? '' : ' closing'}`}
      role="dialog"
      aria-modal="true"
      aria-label="Your phone"
      aria-hidden={open ? undefined : true}
      inert={!open}
    >
      <div className="phone-backdrop" onClick={onClose} />
      <div
        className={`phone-frame${pull !== null ? ' pulling' : ''}`}
        style={pull ? { transform: `translateY(${pull}px)` } : undefined}
      >
        <button type="button" className="phone-close" aria-label="Close phone" onClick={onClose}>
          <X size={16} strokeWidth={2.6} aria-hidden="true" /> Close
        </button>
        <div className="phone-display">
          <div
            className="phone-status"
            onPointerDown={grab}
            onPointerMove={drag}
            onPointerUp={release}
            onPointerCancel={release}
          >
            <span className="phone-status-time">{time}</span>
            <span className="phone-island" aria-hidden="true" />
            <span className="phone-status-signal" aria-label="Signal and battery">
              <small aria-hidden="true">●●●</small> 5G <i aria-hidden="true" />
            </span>
          </div>
          <div className="phone-wallpaper">
            <Skyline />
            <div className="phone-body">
              <div className="phone-clock">
                <strong>{time}</strong>
                <span>{londonDate(now)} · London</span>
              </div>
              <ul className="phone-apps">
                {apps.map((app) => (
                  <li key={app.id}>
                    <button
                      type="button"
                      className="phone-app"
                      onClick={app.onOpen}
                      aria-label={app.badge ? `${app.label}, ${app.badge} new` : app.label}
                    >
                      <span className={`phone-app-icon ${app.tone}`} aria-hidden="true">
                        {app.icon}
                        {!!app.badge && <b>{app.badge > 99 ? '99+' : app.badge}</b>}
                      </span>
                      <span className="phone-app-label">{app.label}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <button type="button" className="phone-home-bar" aria-label="Put the phone away" onClick={onClose}>
            <span />
          </button>
        </div>
      </div>
    </div>
  );
}

// London along the bottom of the wallpaper: the Eye, Big Ben, St Paul's, the Gherkin and the Shard, in
// silhouette among the rooftops
function Skyline() {
  return (
    <svg className="phone-skyline" viewBox="0 0 200 64" preserveAspectRatio="none" aria-hidden="true">
      <circle cx="30" cy="38" r="15" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M30 38 L22 64 M30 38 L38 64" stroke="currentColor" strokeWidth="1.6" />
      <path
        fill="currentColor"
        d="M0 64 V50 h8 v-4 h8 v18 Z M44 64 V48 h6 V24 l3 -8 l3 8 v24 h6 V64 Z M66 64 V46 h8 v-4 q8 -10 16 0 v4 h8 V64 Z M100 64 V44 h10 v20 Z M112 64 V36 q0 -12 7 -14 q7 2 7 14 V64 Z M130 64 V50 h8 V64 Z M140 64 L152 8 L164 64 Z M166 64 V42 h10 v-6 h8 v28 Z M186 64 V48 h14 V64 Z"
      />
    </svg>
  );
}
